import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Modal from '../components/common/Modal';
import './UserManagementPage.css';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect if not super admin
  useEffect(() => {
    if (user && user.user_type !== 'super_admin') {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/users/?skip=${skip}&limit=${limit}`);
      const data = response.data;
      
      const userList = Array.isArray(data) ? data : (data.items || []);
      setUsers(userList);
      
      // If we received fewer items than limit, we've reached the end
      if (userList.length < limit) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && user.user_type === 'super_admin') {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, skip, limit]);

  const handleNextPage = () => {
    if (hasMore) {
      setSkip(prevSkip => prevSkip + limit);
    }
  };

  const handlePrevPage = () => {
    if (skip > 0) {
      setSkip(prevSkip => Math.max(0, prevSkip - limit));
    }
  };

  const openUserModal = (userData) => {
    setSelectedUser(userData);
    setIsModalOpen(true);
  };

  const closeUserModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const filteredUsers = users.filter(userData => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (userData.first_name || '').toLowerCase().includes(term) ||
      (userData.last_name || '').toLowerCase().includes(term) ||
      (userData.username || '').toLowerCase().includes(term) ||
      (userData.email || '').toLowerCase().includes(term) ||
      (userData.city || '').toLowerCase().includes(term)
    );
  });

  if (!user || user.user_type !== 'super_admin') {
    return (
      <div className="user-management-container">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>Super admin privileges required to view this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-container">
      {/* Premium Hero Header */}
      <div className="um-hero">
        <div className="um-hero-content">
          <div className="um-hero-left">
            <h1>User Management</h1>
            <p className="um-hero-subtitle">View and manage platform users</p>
          </div>
          <button 
            onClick={() => navigate('/dashboard/super-admin')} 
            className="back-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="um-content">
        {/* Search & Info Bar */}
        <div className="um-toolbar">
          <div className="um-search-wrapper">
            <svg className="um-search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              className="um-search-input"
              placeholder="Search by name, email, username or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="um-search-clear" onClick={() => setSearchTerm('')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
          <div className="um-user-count">
            <span className="um-count-number">{filteredUsers.length}</span>
            <span className="um-count-label">users</span>
          </div>
        </div>

        {loading && users.length === 0 ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading users...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <h2>Error Loading Users</h2>
            <p>{error}</p>
            <button onClick={fetchUsers} className="retry-btn">
              Try Again
            </button>
          </div>
        ) : (
          <>
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Username</th>
                    <th>Email</th>
                    <th>User Type</th>
                    <th>City</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((userData, index) => (
                      <tr key={userData.id} className={index % 2 === 0 ? 'row-even' : 'row-odd'}>
                        <td className="td-name">{userData.first_name || '-'}</td>
                        <td className="td-name">{userData.last_name || '-'}</td>
                        <td className="td-username">{userData.username || '-'}</td>
                        <td className="td-email">{userData.email || '-'}</td>
                        <td>
                          <span className={`user-type-badge type-${userData.user_type}`}>
                            <span className="badge-dot"></span>
                            {userData.user_type?.replace('_', ' ') || 'Unknown'}
                          </span>
                        </td>
                        <td>{userData.city || '-'}</td>
                        <td>
                          <button 
                            className="view-details-btn"
                            onClick={() => openUserModal(userData)}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data">
                        {searchTerm ? 'No users match your search' : 'No users found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="pagination-controls">
              <button 
                onClick={handlePrevPage} 
                disabled={skip === 0 || loading}
                className="pagination-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
                Previous
              </button>
              <span className="page-info">
                Showing <strong>{skip + 1}</strong> - <strong>{skip + users.length}</strong>
              </span>
              <button 
                onClick={handleNextPage} 
                disabled={!hasMore || loading}
                className="pagination-btn"
              >
                Next
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={closeUserModal}
        title="User Details"
        size="medium"
        closeOnBackdrop={true}
        closeOnEsc={true}
      >
        {selectedUser && (
          <div className="user-details-modal">
            <div className="modal-user-header">
              <div className="modal-avatar">
                {(selectedUser.first_name || selectedUser.username || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="modal-user-info">
                <h3>{selectedUser.first_name} {selectedUser.last_name}</h3>
                <span className={`user-type-badge type-${selectedUser.user_type}`}>
                  <span className="badge-dot"></span>
                  {selectedUser.user_type?.replace('_', ' ')}
                </span>
              </div>
              <span className={`modal-status ${selectedUser.is_active ? 'status-active' : 'status-inactive'}`}>
                <span className="status-dot"></span>
                {selectedUser.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>

            <div className="modal-details-grid">
              <div className="detail-group">
                <label>Username</label>
                <span>{selectedUser.username}</span>
              </div>
              <div className="detail-group">
                <label>Email</label>
                <span>{selectedUser.email}</span>
              </div>
              <div className="detail-group">
                <label>City</label>
                <span>{selectedUser.city || 'N/A'}</span>
              </div>
              <div className="detail-group">
                <label>Website</label>
                <span>
                  {selectedUser.website ? (
                    <a href={selectedUser.website} target="_blank" rel="noopener noreferrer">
                      {selectedUser.website}
                    </a>
                  ) : 'N/A'}
                </span>
              </div>
            </div>

            <div className="detail-group detail-full">
              <label>Bio</label>
              <p className="bio-text">{selectedUser.bio || 'No bio provided.'}</p>
            </div>

            <div className="detail-group detail-full">
              <label>Social Links</label>
              {selectedUser.social_links && Object.keys(selectedUser.social_links).length > 0 ? (
                <ul className="social-links-list">
                  {Object.entries(selectedUser.social_links).map(([platform, link]) => (
                    <li key={platform}>
                      <strong>{platform}: </strong>
                      <a href={link} target="_blank" rel="noopener noreferrer">{link}</a>
                    </li>
                  ))}
                </ul>
              ) : (
                <span>None</span>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default UserManagementPage;